import React, { useState } from "react";
import { FileText } from "lucide-react";
import { apiClient } from "../../utils/apiClient/index.ts";
import { useAlert } from "../../hooks/useAlert";
import Breadcrumb from "../../components/common/common/Breadcrumb";
import { useOrg } from "../../contexts/OrgContext.tsx";
import {
  RequestTemplateEditor,
  FormTemplateData,
} from "./RequestTemplateEditor";
import { RequestType, RoutingType, FieldType } from "@secure-caseworker/shared";

export const CreateRequestTemplatePage: React.FC = () => {
  const { setAlert } = useAlert();
  const { orgId, navigateOrg } = useOrg();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (data: FormTemplateData) => {
    if (!orgId) {
      setAlert({
        variant: "error",
        title: "Error",
        message: "Organization not found",
      });
      return;
    }

    setLoading(true);
    try {
      // Use signer roles from form data for signature templates
      const signatureSigners = data.signerRoles?.map((signer) => ({
        key: signer.key,
        label: signer.label,
        signing_order: signer.signing_order,
        required: signer.required,
      })) || [
        {
          key: "signer_1",
          label: "Signer",
          signing_order: null,
          required: true,
        },
      ];

      const template = await apiClient.createRequestTemplate({
        type: data.type === "form" ? RequestType.FORM : RequestType.SIGNATURE,
        title: data.title,
        description: data.description,
        instructions: data.instructions,
        organization_id: orgId,
        ...(data.type === "form" && {
          form_fields: data.fields.map((f, index) => ({
            key: f.id,
            label: f.label,
            field_type: f.field_type as FieldType,
            required: f.required,
            options: f.options ?? undefined,
            order: index,
          })),
        }),
        ...(data.type === "signature" && {
          routing:
            data.routing === "sequential"
              ? RoutingType.SEQUENTIAL
              : RoutingType.PARALLEL,
          signature_signers: signatureSigners,
        }),
      });

      // Upload PDFs with signature places for signature templates (single API call)
      if (
        data.type === "signature" &&
        data.pdfDocuments &&
        data.pdfDocuments.length > 0
      ) {
        await apiClient.uploadTemplatePdfs(
          template.id,
          orgId,
          data.pdfDocuments,
        );
      }

      setAlert({
        variant: "success",
        title: "Success",
        message: "Template created successfully",
      });
      navigateOrg("/request-templates");
    } catch (error) {
      setAlert({
        variant: "error",
        title: "Error",
        message:
          error instanceof Error ? error.message : "Failed to create template",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigateOrg("/request-templates");
  };

  return (
    <div className="space-y-6">
      <Breadcrumb
        items={[
          { label: "Request Templates", path: "/request-templates" },
          { label: "New Template" },
        ]}
      />

      <div className="flex items-center gap-3">
        <FileText size={32} className="text-brand-500" />
        <div>
          <h1 className="text-title-md2 font-semibold text-black dark:text-white">
            Create New Request Template
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Create a reusable template for your organization
          </p>
        </div>
      </div>

      <RequestTemplateEditor
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        submitLabel="Create Template"
        isLoading={loading}
      />
    </div>
  );
};
